import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchCategories } from "../redux/productSlice";
import { toast } from "react-hot-toast";

const ManageCategories = () => {
  const dispatch = useDispatch();
  const token = useSelector((state) => state.user.token);
  const categories = useSelector((state) => state.product.categoryList);

  const [formData, setFormData] = useState({
    name: "",
    value: "",
    priority: 999,
  });
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState("");

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      // Auto-generate value (slug) if name is changed and not in edit mode
      ...(name === "name" && !editMode
        ? { value: value.toLowerCase().replace(/[^a-z0-9]/g, "") }
        : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.value) {
      toast.error("Name and Value are required");
      return;
    }

    try {
      const url = editMode
        ? `${process.env.REACT_APP_SERVER_DOMIN}/categories/${selectedId}`
        : `${process.env.REACT_APP_SERVER_DOMIN}/categories`;
      const method = editMode ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setFormData({ name: "", value: "", priority: 999 });
        setEditMode(false);
        setSelectedId("");
        dispatch(fetchCategories()); // Refresh list
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error("Failed to save category");
    }
  };

  const handleEdit = (category) => {
    setEditMode(true);
    setSelectedId(category._id);
    setFormData({
      name: category.name,
      value: category.value,
      priority: category.priority,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to archive this category?")) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_DOMIN}/categories/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        dispatch(fetchCategories()); // Refresh list
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setSelectedId("");
    setFormData({ name: "", value: "", priority: 999 });
  };

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-center text-[#5C3A21] mb-8">Manage Categories</h1>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Form Section */}
        <div className="md:w-1/3 bg-white p-6 rounded-xl shadow-md border border-purple-100 h-fit">
          <h2 className="text-xl font-semibold mb-4 text-[#2D2D2D]">
            {editMode ? "Edit Category" : "Add New Category"}
          </h2>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleOnChange}
                className="w-full p-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:outline-none"
                placeholder="e.g. Seasonal Treats"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Internal Value (Slug)</label>
              <input
                type="text"
                name="value"
                value={formData.value}
                onChange={handleOnChange}
                className={`w-full p-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:outline-none ${editMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="e.g. seasonaltreats"
                required
                readOnly={editMode} // Best practice: don't change slug if editing to avoid breaking links
              />
              <p className="text-xs text-gray-500 mt-1">Used internally and in URLs. No spaces or special characters.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority (Sorting)</label>
              <input
                type="number"
                name="priority"
                value={formData.priority}
                onChange={handleOnChange}
                className="w-full p-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">Lower numbers appear first (e.g., 1 is first).</p>
            </div>

            <div className="flex gap-3 mt-4">
              <button
                type="submit"
                className="flex-1 bg-gradient-to-r from-pink-400 to-purple-400 hover:from-pink-500 hover:to-purple-500 text-white font-semibold py-2 px-4 rounded-lg shadow transition-all"
              >
                {editMode ? "Update" : "Create"}
              </button>
              {editMode && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg shadow transition-all"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* List Section */}
        <div className="md:w-2/3 bg-white p-6 rounded-xl shadow-md border border-purple-100">
          <h2 className="text-xl font-semibold mb-4 text-[#2D2D2D]">Existing Categories</h2>
          {categories.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-purple-50 text-gray-700 border-b border-purple-100">
                    <th className="p-3 font-semibold rounded-tl-lg">Priority</th>
                    <th className="p-3 font-semibold">Display Name</th>
                    <th className="p-3 font-semibold">Value / Slug</th>
                    <th className="p-3 font-semibold text-center rounded-tr-lg">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr key={cat._id} className="border-b border-gray-100 hover:bg-pink-50/30 transition">
                      <td className="p-3 font-mono text-sm">{cat.priority}</td>
                      <td className="p-3 font-medium text-gray-800">{cat.name}</td>
                      <td className="p-3 text-gray-500 text-sm">{cat.value}</td>
                      <td className="p-3 flex gap-2 justify-center">
                        <button
                          onClick={() => handleEdit(cat)}
                          className="bg-blue-100 hover:bg-blue-200 text-blue-700 py-1 px-3 rounded text-sm transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(cat._id)}
                          className="bg-red-100 hover:bg-red-200 text-red-700 py-1 px-3 rounded text-sm transition"
                        >
                          Archive
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No categories found. Create one above!</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageCategories;
